import { Autocomplete, Chip, TextField } from '@mui/material';
import {
  categoryOptions,
  countryOptions,
  isoDateToUk,
  ukDateToIso,
  useTaxonomies,
  type TermOption,
} from '../lib/taxonomies';

// Dropdowns backed by the WBD taxonomy term lists. A stored value that isn't in
// the list (legacy free text from Evessio, e.g. "UK") is still shown — grouped
// under "Not in WBD list" — so opening the form never silently drops data; the
// user just can't pick new off-list values.

const OFF_LIST = 'Not in WBD list';

function withCurrent(options: TermOption[], current: string[]): TermOption[] {
  const known = new Set(options.map((o) => o.name));
  const extra = current.filter((v) => v && !known.has(v)).map((name) => ({ name, group: OFF_LIST }));
  return extra.length ? [...extra, ...options] : options;
}

// Group headers only when they mean something: WBD parents (categories), or to
// set off-list values apart. Otherwise a flat list with no blank headers.
function groupBy(all: TermOption[], grouped?: boolean) {
  if (grouped) return (o: TermOption) => o.group || o.name;
  if (all.some((o) => o.group === OFF_LIST)) return (o: TermOption) => (o.group === OFF_LIST ? OFF_LIST : 'WBD terms');
  return undefined;
}

interface TermSelectProps {
  value: string;
  options: TermOption[];
  onChange: (value: string) => void;
  /** Group options under their WBD parent term (categories). */
  grouped?: boolean;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
}

export function TermSelect({ value, options, onChange, grouped, placeholder, disabled, label }: TermSelectProps) {
  const all = withCurrent(options, [value]);
  const selected = all.find((o) => o.name === value) ?? null;
  return (
    <Autocomplete
      size="small"
      fullWidth
      disabled={disabled}
      options={all}
      value={selected}
      groupBy={groupBy(all, grouped)}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.name === b.name}
      onChange={(_e, o) => onChange(o?.name ?? '')}
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder ?? 'Select…'} />}
    />
  );
}

interface TermMultiSelectProps {
  value: string[];
  options: TermOption[];
  onChange: (value: string[]) => void;
  grouped?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function TermMultiSelect({ value, options, onChange, grouped, placeholder, disabled }: TermMultiSelectProps) {
  const all = withCurrent(options, value);
  const selected = value.map((v) => all.find((o) => o.name === v)!).filter(Boolean);
  return (
    <Autocomplete
      multiple
      size="small"
      fullWidth
      disableCloseOnSelect
      disabled={disabled}
      options={all}
      value={selected}
      groupBy={groupBy(all, grouped)}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.name === b.name}
      onChange={(_e, opts) => onChange(opts.map((o) => o.name))}
      renderTags={(opts, getTagProps) =>
        opts.map((o, i) => {
          const { key, ...rest } = getTagProps({ index: i });
          return <Chip key={key} size="small" label={o.name} {...rest} />;
        })
      }
      renderInput={(params) => (
        <TextField {...params} placeholder={value.length ? undefined : (placeholder ?? 'Select…')} />
      )}
    />
  );
}

interface WbdFieldInputProps {
  /** Nomination column name, e.g. "category_name". */
  field: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  label?: string;
  disabled?: boolean;
}

/**
 * Edit input for one entry field: WBD dropdowns for category and the two
 * countries (so values match WBD's terms), a date picker for the completion
 * date (stored DD/MM/YYYY), a text box for everything else. Falls back to a
 * text box until the WBD term lists have loaded.
 */
export function WbdFieldInput({ field, value, onChange, multiline, label, disabled }: WbdFieldInputProps) {
  const tax = useTaxonomies();
  if (tax && field === 'category_name')
    return <TermSelect grouped label={label} value={value} options={categoryOptions(tax)} onChange={onChange} disabled={disabled} />;
  if (tax && field === 'project_country')
    return <TermSelect label={label} value={value} options={countryOptions(tax, 'project')} onChange={onChange} disabled={disabled} />;
  if (tax && field === 'architect_company_country')
    return <TermSelect label={label} value={value} options={countryOptions(tax, 'architect')} onChange={onChange} disabled={disabled} />;
  if (field === 'project_completion_date') {
    const iso = ukDateToIso(value);
    return (
      <TextField
        type="date"
        size="small"
        fullWidth
        label={label}
        disabled={disabled}
        value={iso}
        onChange={(e) => onChange(isoDateToUk(e.target.value))}
        slotProps={{ inputLabel: { shrink: true } }}
        helperText={value && !iso ? `Currently "${value}" (not DD/MM/YYYY) — pick a date to replace it` : undefined}
      />
    );
  }
  return (
    <TextField
      size="small"
      fullWidth
      label={label}
      disabled={disabled}
      multiline={multiline}
      minRows={multiline ? 3 : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
